import { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Form, Button, Card, Row, Col, ListGroup, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import { FaFacebookF, FaInstagram, FaYoutube, FaTwitter, FaTiktok } from 'react-icons/fa';
import { JSX } from 'react/jsx-runtime';

const RequestForm = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedArtistId, setSelectedArtistId] = useState('');
  const [selectedSongId, setSelectedSongId] = useState('');
  const [artists, setArtists] = useState<any[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [textOptIn, setTextOptIn] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/songs/artists`)
      .then(res => setArtists(res.data))
      .catch(err => console.error('Failed to fetch artists:', err));
  }, []);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const validatePhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.length === 10;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formatted = formatPhoneNumber(rawValue);
    setPhone(formatted);
    if (!validatePhoneNumber(rawValue)) {
      setPhoneError('Please enter a valid 10-digit phone number');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // if (!validatePhoneNumber(phone)) {
    //   setPhoneError('Please enter a valid 10-digit phone number');
    //   return;
    // }
    const payload = {
      name,
      phoneNumber: phone.replace(/\D/g, ''),
      songId: Number(selectedSongId)
    };
    axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/songrequests`, payload)
      .then(response => {
        console.log('Request submitted, ID:', response.data.id);
        setName('');
        setPhone('');
        setSelectedArtistId('');
        setSelectedSongId('');
        setPhoneError('');
        setTextOptIn(false);
        setShowConfirmation(true);
        setTimeout(() => setShowConfirmation(false), 3000);
      })
      .catch(error => {
        console.error('Submission error:', error.response?.data || error.message);
        alert(`Failed to submit request: ${error.response?.data || 'Unknown error'}`);
      });
  };

  const handleArtistClick = (artistId: string, e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedArtistId(artistId);
    setSelectedSongId('');
  };

  const handleSongClick = (songId: string, e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedSongId(songId);
  };

  const selectedArtist = artists.find(a => a.id === Number(selectedArtistId));
  const songs = selectedArtist?.songs || [];

  return (
    <Container fluid className="mt-3 mb-5">
      {/* Rules and Social Links Section */}
      <div className="rules-social-container animate__animated animate__zoomIn">
        <h3 className="rules-title">
          <span className="mic-icon">🎤</span> LIVE KARAOKE RULES <span className="mic-icon">🎤</span>
        </h3>
        <ul className="rules-list">
          <li>
            <span className="rule-title">Follow Us</span>
            <p className="rule-description">
              Please tag us in any videos or photos you get during the night. We love enjoying the show from your point of view.
            </p>
            <div className="social-icons mt-2">
              <a href="https://twitter.com/ItsAllGoodBand" target="_blank" rel="noopener noreferrer" className="social-icon mx-2" aria-label="Follow us on X">
                {FaTwitter({ size: 24 }) as JSX.Element}
              </a>
              <a href="https://www.youtube.com/channel/UCXHvGenl4k8ilrvaohLBTIw" target="_blank" rel="noopener noreferrer" className="social-icon mx-2" aria-label="Follow us on YouTube">
                {FaYoutube({ size: 24 }) as JSX.Element}
              </a>
              <a href="https://www.instagram.com/itsallgoodband/" target="_blank" rel="noopener noreferrer" className="social-icon mx-2" aria-label="Follow us on Instagram">
                {FaInstagram({ size: 24 }) as JSX.Element}
              </a>
              <a href="https://www.facebook.com/itsallgoodmusic" target="_blank" rel="noopener noreferrer" className="social-icon mx-2" aria-label="Follow us on Facebook">
                {FaFacebookF({ size: 24 }) as JSX.Element}
              </a>
              <a href="https://vm.tiktok.com/ZMe5yVwda/" target="_blank" rel="noopener noreferrer" className="social-icon mx-2" aria-label="Follow us on TikTok">
                {FaTiktok({ size: 24 }) as JSX.Element}
              </a>
            </div>
          </li>
          <li>
            <span className="rule-title">No Drinks on Stage</span>
            <p className="rule-description">Leave it with a trustworthy friend :)</p>
          </li>
          <li>
            <span className="rule-title">Be Nice and Have Fun!</span>
            <p className="rule-description">We’re all family, act like it.</p>
          </li>
        </ul>
      </div>

      {/* Main Form Card */}
      <Card className="card-custom">
        <Card.Header className="text-center py-4" style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white' }}>
          <div className="mb-3 animate__animated animate__fadeIn">
            <img
              src="/logo.png"
              alt="Karaoke Band Logo"
              style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }}
            />
          </div>
          <h2 className="mb-0" style={{ fontFamily: "'blowbrush', sans-serif", letterSpacing: '2.5px' }}>
            SIGN UP TO SING!
          </h2>
        </Card.Header>
        <Card.Body className="p-4">
          {showConfirmation && (
            <Alert
              variant="success"
              className="mb-3 animate__animated animate__fadeIn"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Song request submitted successfully!
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <Row className="g-2 mb-3 animate__animated animate__fadeIn">
              <Col xs={12}>
                <Form.Group controlId="formName">
                  <Form.Label style={{ fontFamily: "'Montserrat', sans-serif" }}>Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter your name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    className="shadow-sm"
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                {/* <Form.Group controlId="formPhone">
                  <Form.Label style={{ fontFamily: "'Montserrat', sans-serif" }}>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="(123) 456-7890"
                    value={phone}
                    onChange={handlePhoneChange}
                    required
                    className="shadow-sm"
                    isInvalid={!!phoneError}
                    maxLength={14}
                  />
                  <Form.Control.Feedback type="invalid">
                    {phoneError}
                  </Form.Control.Feedback>
                </Form.Group> */}
                {/* <Form.Group controlId="formTextOptIn" className="mt-2">
                  <Form.Check
                    type="checkbox"
                    label="I agree to receive text message notifications about my performance time."
                    checked={textOptIn}
                    onChange={(e) => setTextOptIn(e.target.checked)}
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  />
                </Form.Group> */}
              </Col>
            </Row>

            <Row className="g-2 mb-3 animate__animated animate__fadeIn" style={{ animationDelay: '0.2s' }}>
              <Col xs={12}>
                <Form.Label style={{ fontFamily: "'Montserrat', sans-serif" }}>Artists</Form.Label>
                <ListGroup style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.9rem', borderRadius: '10px' }}>
                  {artists.map((artist, index) => (
                    <ListGroup.Item
                      key={artist.id}
                      action
                      active={selectedArtistId === artist.id.toString()}
                      onClick={(e) => handleArtistClick(artist.id.toString(), e)}
                      className="py-2 animate__animated animate__slideInUp"
                      style={{ animationDelay: `${0.1 * index}s` }}
                    >
                      {artist.name}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Col>

              <Col xs={12}>
                <Form.Label style={{ fontFamily: "'Montserrat', sans-serif" }}>Songs</Form.Label>
                <ListGroup style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.9rem', borderRadius: '10px' }}>
                  {songs.length > 0 ? (
                    songs.map((song: any, index: number) => (
                      <ListGroup.Item
                        key={song.id}
                        action
                        active={selectedSongId === song.id.toString()}
                        onClick={(e) => handleSongClick(song.id.toString(), e)}
                        className="py-2 animate__animated animate__slideInUp"
                        style={{ animationDelay: `${0.1 * index}s` }}
                      >
                        {song.title}
                      </ListGroup.Item>
                    ))
                  ) : (
                    <ListGroup.Item className="py-2">
                      {selectedArtistId ? 'No songs available' : 'Select an artist'}
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Col>
            </Row>

            <Button
              className="btn-custom-primary w-100 py-2 animate__animated animate__fadeIn"
              type="submit"
              // disabled={!selectedSongId || !!phoneError || !textOptIn}
              disabled={!selectedSongId}
              style={{ animationDelay: '0.4s' }}
            >
              Submit
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Footer Links */}
      <footer className="footer-links animate__animated animate__fadeInUp">
        <div className="merch-section">
          <a href="https://www.itsallgoodband.com/" target="_blank" rel="noopener noreferrer" className="merch-link1">
            <h4 className="merch-title">🎸 CHECK OUT OUR MERCH STORE! 🎸</h4>
          </a>
          <a href="https://www.itsallgoodband.com/" target="_blank" rel="noopener noreferrer" className="merch-link">
            Use Code "KARAOKE" for a 10% Discount!
          </a>
        </div>
        <div className="band-section">
          <a href="https://www.itsallgoodband.com/" target="_blank" rel="noopener noreferrer" className="band-link">
            🌟 Visit It’s All Good Band! 🌟
          </a>
        </div>
      </footer>
    </Container>
  );
};

export default RequestForm;